import {
    computed,
    effect,
    EnvironmentProviders,
    inject,
    Injectable,
    Injector,
    makeEnvironmentProviders,
    provideEnvironmentInitializer,
    untracked,
} from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { ApplicationContextStore } from '../application-context/application-context.store';
import { settingState } from '../application-context/setting-state';
import { ApplicationInsightsService } from './application-insights.service';

/**
 * Decides when client telemetry runs, so every shell applies the same rule: only once a user has signed in, and only
 * while the selected branch has the watched Boolean setting on. Shells wire it with {@link provideTelemetryPolicy}
 * and never call {@link ApplicationInsightsService.start} or {@link ApplicationInsightsService.stop} themselves.
 */
@Injectable({ providedIn: 'root' })
export class TelemetryPolicyService {
    private readonly applicationContext = inject(ApplicationContextStore);
    private readonly appInsights = inject(ApplicationInsightsService);
    private readonly injector = inject(Injector);
    private readonly startedSubject = new Subject<void>();

    /** Whether a sign-in has been seen this app session; signing out after one stops telemetry, before one it does not. */
    private hasSignedIn = false;

    /** Whether the policy has asked telemetry to start, so a setting that later goes missing stops it (fail-closed). */
    private telemetryRequested = false;

    private watching = false;

    /**
     * Emits each time telemetry has started running under this policy (after a sign-in, or after the setting turns on).
     * Shells use it for events that must only ever be sent once telemetry is allowed, such as a device login event.
     */
    readonly started: Observable<void> = this.startedSubject.asObservable();

    /**
     * Starts applying the policy for the given setting. Idempotent; the first key wins.
     *
     * @param settingKey The Boolean branch setting (a SettingKey enum member name) that allows telemetry.
     */
    watch(settingKey: string): void {
        if (this.watching) {
            return;
        }
        this.watching = true;
        const allowed = computed(() => settingState(this.applicationContext.settings(), settingKey));
        effect(() => this.apply(this.applicationContext.isLoggedIn(), allowed()), { injector: this.injector });
    }

    /**
     * Starts telemetry once signed in on a branch that allows it, and stops it on sign-out or when the branch has it
     * off. Nothing is sent before sign-in: until then the service only queues exceptions, so start-up errors reach
     * telemetry only if the signed-in branch allows it. That is also why it never stops before the first sign-in: the
     * store reports signed-out while it restores the session at boot, and stopping then would discard that queue. A
     * missing setting (settings not loaded yet) starts nothing and stops telemetry only if it was asked to run, so the
     * queue survives the gap between sign-in and the settings arriving.
     *
     * The same restraint protects telemetry kept on the device while offline: every stop deletes it (see
     * {@link ApplicationInsightsService.stop}), so items an earlier run left behind are sent only if this session's
     * branch starts telemetry, and deleted once it is signed out or its branch has telemetry off.
     */
    private apply(isLoggedIn: boolean, allowed: boolean | undefined): void {
        if (!isLoggedIn) {
            if (this.hasSignedIn) {
                this.stop();
            }
            return;
        }
        this.hasSignedIn = true;

        if (allowed === true) {
            this.telemetryRequested = true;
            const authenticatedUserId = untracked(() => this.applicationContext.userId());
            this.appInsights.start({ authenticatedUserId }).then((running) => {
                if (running && this.telemetryRequested) {
                    this.startedSubject.next();
                }
            });
        } else if (allowed === false || this.telemetryRequested) {
            this.stop();
        }
    }

    private stop(): void {
        this.telemetryRequested = false;
        this.appInsights.stop();
    }
}

/**
 * Applies the {@link TelemetryPolicyService} from application start, watching the given Boolean branch setting.
 *
 * @param settingKey The setting (a SettingKey enum member name) that allows telemetry for the selected branch.
 * @returns Providers for the application config.
 */
export function provideTelemetryPolicy(settingKey: string): EnvironmentProviders {
    return makeEnvironmentProviders([provideEnvironmentInitializer(() => inject(TelemetryPolicyService).watch(settingKey))]);
}
